/**
 * Onboarding step 5 — Team. Collect + validate invite emails with roles
 * (the same AppUserRole vocabulary the admin invite flow uses). Nothing
 * is sent from here: on activation each row is queued as the pending
 * `appUsers` record the existing first-sign-in provisioning understands.
 */

import { Plus, Trash2 } from 'lucide-react';
import type { AppUserRole } from '../../../lib/db/types';
import type { DraftInvite, OnboardingDraft } from '../../../lib/onboarding';

const ROLES: { value: AppUserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'staff', label: 'Staff' },
  { value: 'viewer', label: 'Viewer' },
];

interface Props {
  draft: OnboardingDraft;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

export function StepTeam({ draft, onPatch }: Props) {
  const setRow = (idx: number, patch: Partial<DraftInvite>) =>
    onPatch({
      invites: draft.invites.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });

  const addRow = () =>
    onPatch({ invites: [...draft.invites, { email: '', role: 'staff' }] });

  const removeRow = (idx: number) =>
    onPatch({ invites: draft.invites.filter((_, i) => i !== idx) });

  return (
    <div className="onb-step">
      <h3 className="t-section-title onb-step__title">Invite your team</h3>
      <p className="t-meta onb-step__blurb">
        Invites are queued as pending users — they land in Admin &gt; Users and
        activate on first sign-in. No emails are sent from this step.
      </p>

      {draft.invites.map((invite, idx) => (
        <div key={idx} className="onb-row onb-repeat-row">
          <div style={{ flex: 2 }}>
            <label className="t-eyebrow onb-label" htmlFor={`onb-invite-email-${idx}`}>
              Email {idx + 1}
            </label>
            <input
              id={`onb-invite-email-${idx}`}
              className="glass-input"
              type="email"
              value={invite.email}
              onChange={(e) => setRow(idx, { email: e.target.value })}
              placeholder="teammate@company.com"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="t-eyebrow onb-label" htmlFor={`onb-invite-role-${idx}`}>
              Role
            </label>
            <select
              id={`onb-invite-role-${idx}`}
              className="glass-input"
              value={invite.role}
              onChange={(e) => setRow(idx, { role: e.target.value as AppUserRole })}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          {draft.invites.length > 1 && (
            <button
              type="button"
              className="glass-btn onb-remove"
              onClick={() => removeRow(idx)}
              aria-label={`Remove invite ${idx + 1}`}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}

      <button type="button" className="glass-btn" onClick={addRow}>
        <Plus size={13} /> Add teammate
      </button>
    </div>
  );
}
